import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Gamepad2, Link2, MessageCircle, Plus } from 'lucide-react';
import {
  GAME_KINDS,
  gameDocName,
  gameKindLabel,
  gamePostPath,
  pacingLabel,
  type GameKind,
  type GameMeta,
} from '@awwyeah/shared';
import { useDocLocalLoaded } from '../../sync';
import { useUser } from '../auth';
import {
  ARCHIVE_GROUP_THRESHOLD,
  finishedDateLabel,
  groupGamesByMonth,
  outcomeLabel,
  sortFinishedNewestFirst,
} from './archive';
import { colorName } from './colorName';
import { KindIcon } from './components/KindIcon';
import { Sheet } from './components/Sheet';
import { useUnreadChat } from './components/chatSeen';
import { createGameMeta, createGuestGameMeta, newId } from './gameOps';
import { apiMintGuestLink } from './guestApi';
import { stashGuestToken } from './guestLinkStash';
import { arrangeActiveGames, type GameListFacts } from './listOrder';
import { useGameSummary } from './useGameMoves';
import { useGamesRegistry } from './useGamesRegistry';
import { useHouseholdRoster, type RosterMember } from './useHouseholdRoster';

/**
 * Games — Connect-4 v1 (WS-17a) + the finished-games archive (WS-17b). Lives *inside* the
 * Apps tab rather than a 7th nav tab (the brief is explicit about this), so this section is
 * rendered directly from `apps/web/src/modules/apps/AppsIndexPage.tsx`. No demand mechanics
 * (`memory/no-demand-mechanics.md`): "your move" is a fact about whose turn it is, never a
 * nag.
 *
 * The Finished section is a proper little archive (`archive.ts`), not just a collapsed
 * list: each entry is a card naming both players, the fact of who won (or a draw), the date
 * it finished, and the move count — tapping it opens `GameBoardPage`, which for a finished
 * game already opens on the final position with a "Watch replay" control. **No tallies,
 * ever** — see `archive.ts`'s docblock for why that's a hard rule here, not an oversight.
 */

function GameRow({
  game,
  opponent,
  me,
  onFacts,
  onUnreadChat,
}: {
  game: GameMeta;
  opponent: RosterMember;
  me: RosterMember;
  onUnreadChat: (gameId: string, unread: boolean) => void;
  /** Reports what only the move log knows back up to the list, which sorts on it. */
  onFacts: (gameId: string, facts: GameListFacts) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const summary = useGameSummary(game, me.id);
  const unreadChat = useUnreadChat(game.id, me.id);
  const hasUnreadChat = unreadChat.count > 0;
  useEffect(() => {
    onUnreadChat(game.id, hasUnreadChat);
  }, [onUnreadChat, game.id, hasUnreadChat]);
  const isMyTurn = summary.isMyTurn;
  const finished = game.status === 'done' || summary.finished;
  const winnerIsMe = summary.winnerIndex !== null && game.playerIds[summary.winnerIndex] === me.id;
  const liveTurn = isMyTurn && !finished;
  // Held until the local copy of the move log has loaded, so an empty not-yet-read log never
  // reports "no moves" and folds a live game into Quiet for a frame.
  const loaded = useDocLocalLoaded(gameDocName(game.id));

  useEffect(() => {
    if (!loaded) return;
    onFacts(game.id, { isMyTurn: liveTurn, lastMoveAt: summary.lastMoveAt });
  }, [loaded, onFacts, game.id, liveTurn, summary.lastMoveAt]);

  return (
    <button
      type="button"
      data-testid="game-row"
      data-game-id={game.id}
      data-my-turn={isMyTurn}
      onClick={() => navigate(gamePostPath(game.id))}
      className="flex min-h-[44px] w-full items-center gap-3 rounded-md border border-line bg-surface p-3 text-left"
    >
      <span
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-semibold"
        style={{ backgroundColor: opponent.color, color: '#FBF8F2' }}
        aria-hidden="true"
      >
        {opponent.avatarEmoji ?? opponent.name.slice(0, 1).toUpperCase()}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-base font-semibold text-fg">{game.title}</span>
        <span className="flex items-center gap-1 truncate text-xs text-fg-3">
          <KindIcon kind={game.kind} size={12} />
          {gameKindLabel(game.kind)} ·{' '}
          {finished
            ? summary.winnerIndex !== null
              ? `Ended — ${winnerIsMe ? 'you won' : `${opponent.name} won`}`
              : 'Ended — draw'
            : `vs ${opponent.name}`}
        </span>
        {/* Your side, so a game you haven't opened in a month still says which pieces are
            yours before you tap in. Same words as the board's own side line. */}
        <span
          className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-fg-2"
          data-testid="game-row-side"
        >
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-line-strong"
            style={{ backgroundColor: me.color }}
            aria-hidden="true"
          />
          You&rsquo;re {colorName(me.color)}
          {summary.sideDetail ? ` · ${summary.sideDetail}` : ''}
        </span>
        {unreadChat.latest && (
          <span
            className="mt-1 flex items-center gap-1.5 truncate text-xs font-semibold text-fg"
            data-testid="game-row-chat"
          >
            <MessageCircle size={12} aria-hidden="true" className="shrink-0" />
            <span className="truncate">
              {opponent.name}: {unreadChat.latest.text}
              {unreadChat.count > 1 ? ` (+${unreadChat.count - 1})` : ''}
            </span>
          </span>
        )}
      </span>
      {!finished && isMyTurn && (
        <span
          data-testid="your-move-badge"
          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-line px-2 py-0.5 text-[11px] font-semibold text-fg"
        >
          Your move
        </span>
      )}
    </button>
  );
}

/** One archive entry: players, the fact of who won (or "Draw"), date finished, move count.
 * Tapping it opens the replay view (`GameBoardPage` opens finished games on their final
 * position with a "Watch replay" control — see WS-17b). */
function FinishedGameCard({
  game,
  roster,
}: {
  game: GameMeta;
  roster: ReturnType<typeof useHouseholdRoster>;
}): JSX.Element {
  const navigate = useNavigate();
  const { moves } = useGameSummary(game, null);
  const [playerA, playerB] = game.playerIds.map((id) => roster.getMember(id, game.guestNames));
  const winnerName = game.winnerId ? roster.getMember(game.winnerId, game.guestNames).name : null;
  const pacing = pacingLabel(moves);

  return (
    <button
      type="button"
      data-testid="finished-game-card"
      data-game-id={game.id}
      onClick={() => navigate(gamePostPath(game.id))}
      className="flex min-h-[44px] flex-col gap-2 rounded-md border border-line bg-surface p-3 text-left"
    >
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1.5">
          <span
            className="h-3.5 w-3.5 shrink-0 rounded-full"
            style={{ backgroundColor: playerA?.color }}
            aria-hidden="true"
          />
          <span className="text-sm font-medium text-fg">{playerA?.name}</span>
        </span>
        <span className="text-xs text-fg-3">vs</span>
        <span className="flex items-center gap-1.5">
          <span
            className="h-3.5 w-3.5 shrink-0 rounded-full"
            style={{ backgroundColor: playerB?.color }}
            aria-hidden="true"
          />
          <span className="text-sm font-medium text-fg">{playerB?.name}</span>
        </span>
        <span
          className="ml-auto flex shrink-0 items-center gap-1 text-xs text-fg-3"
          data-testid="finished-game-date"
        >
          <KindIcon kind={game.kind} size={12} />
          {finishedDateLabel(game)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-base font-semibold text-fg" data-testid="finished-game-outcome">
          {outcomeLabel(game, winnerName)}
        </span>
        <span className="shrink-0 text-xs text-fg-3">
          {moves.length} move{moves.length === 1 ? '' : 's'}
          {pacing ? ` · ${pacing}` : ''}
        </span>
      </div>
    </button>
  );
}

function StartGameSheet({
  open,
  onClose,
  userId,
  userName,
  members,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  members: RosterMember[];
  onCreated: (meta: GameMeta) => void;
}): JSX.Element {
  const registries = useGamesRegistry();
  const navigate = useNavigate();
  // Pick the game, then pick the person — the same order you'd say it out loud.
  const [kind, setKind] = useState<GameKind>('connect4');
  const [inviting, setInviting] = useState(false);

  const start = (opponent: RosterMember): void => {
    const meta = createGameMeta({
      kind,
      starterId: userId,
      starterName: userName,
      opponentId: opponent.id,
      opponentName: opponent.name,
    });
    registries.map.set(meta.id, meta);
    onCreated(meta);
    onClose();
    navigate(gamePostPath(meta.id));
  };

  /**
   * "Invite by link": mint first, create second (WS-23). The mint call is what allocates the
   * guest id, and seat 2 needs a real id before the game exists at all — so the game is
   * created *around* the id the server just handed back, rather than being patched afterwards
   * (see `guestCore.ts` for why that ordering is forced).
   */
  const startWithLink = async (): Promise<void> => {
    setInviting(true);
    try {
      const gameId = newId();
      const minted = await apiMintGuestLink(gameId);
      stashGuestToken(gameId, minted.token);
      const meta = createGuestGameMeta({
        id: gameId,
        kind,
        starterId: userId,
        starterName: userName,
        guestId: minted.guestId,
      });
      registries.map.set(meta.id, meta);
      onCreated(meta);
      onClose();
      navigate(gamePostPath(meta.id));
    } finally {
      setInviting(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Start a game" testId="new-game-sheet">
      <div className="flex flex-col gap-4 pb-2">
        {/* Wraps rather than squeezing: five kinds in one row on a 375px phone is five
            unreadable slivers, and the list only grows from here. */}
        <div className="flex flex-wrap gap-2" data-testid="kind-picker">
          {GAME_KINDS.map((option) => (
            <button
              key={option}
              type="button"
              data-testid="game-kind-option"
              data-kind={option}
              data-selected={option === kind}
              onClick={() => setKind(option)}
              className={`flex h-12 min-w-[7rem] flex-1 items-center justify-center gap-2 rounded-md border text-sm font-semibold ${
                option === kind
                  ? 'border-line-strong bg-inverse text-inverse-fg'
                  : 'border-line text-fg-2'
              }`}
            >
              <KindIcon kind={option} size={16} />
              {gameKindLabel(option)}
            </button>
          ))}
        </div>
        <p className="text-sm text-fg-2">
          {kind === 'battleship'
            ? 'Battleship · place your fleets, then take turns. Neither of you can see the other’s ships.'
            : kind === 'checkers'
              ? 'Checkers · forced captures, multi-jumps, kings. Classic rules.'
              : kind === 'pool'
                ? '8-ball Pool · drag back from the cue ball, let go.'
                : kind === 'beerpong'
                  ? 'Beer Pong · ten cups each, one throw a turn. Clear their side to win.'
                  : kind === 'mancala'
                    ? 'Mancala · sow your beads around the board. Land in your store to go again. Play together on one phone, or each on your own.'
                    : 'Connect-4 · pick who you’re playing.'}
        </p>
        <div className="flex flex-col gap-2">
          {members.length === 0 && (
            <p className="text-sm text-fg-3">No one else is on the household yet.</p>
          )}
          {members.map((member) => (
            <button
              key={member.id}
              type="button"
              data-testid="start-game-opponent"
              data-member-id={member.id}
              onClick={() => start(member)}
              className="flex h-14 items-center gap-3 rounded-md border border-line px-3 text-left"
            >
              <span
                className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold"
                style={{ backgroundColor: member.color, color: '#FBF8F2' }}
                aria-hidden="true"
              >
                {member.avatarEmoji ?? member.name.slice(0, 1).toUpperCase()}
              </span>
              <span className="text-base font-medium text-fg">{member.name}</span>
            </button>
          ))}
          <button
            type="button"
            data-testid="start-game-invite-link"
            disabled={inviting}
            onClick={() => void startWithLink()}
            className="flex h-14 items-center gap-3 rounded-md border border-dashed border-line px-3 text-left disabled:opacity-50"
          >
            <span
              className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-fg-2"
              aria-hidden="true"
            >
              <Link2 size={16} />
            </span>
            <span className="flex flex-col">
              <span className="text-base font-medium text-fg">Invite by link</span>
              <span className="text-xs text-fg-3">
                For someone outside the house — whoever opens it first plays.
              </span>
            </span>
          </button>
        </div>
      </div>
    </Sheet>
  );
}

const GAMES_OPEN_STORAGE_KEY = 'awwyeah:apps:games-open';
const QUIET_OPEN_STORAGE_KEY = 'awwyeah:apps:games-quiet-open';

function readOpen(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : raw === '1';
  } catch {
    return fallback;
  }
}

function writeOpen(key: string, open: boolean): void {
  try {
    localStorage.setItem(key, open ? '1' : '0');
  } catch {
    // Private mode — the fold just won't remember itself.
  }
}

/**
 * `defaultOpen` is the first-visit state; the fold remembers itself per device after that
 * (2026-09-08: folded by default so the Apps tab leads with the daily tools).
 */
export function GamesSection({
  defaultOpen = true,
  standalone = false,
}: { defaultOpen?: boolean; standalone?: boolean } = {}): JSX.Element | null {
  const { user } = useUser();
  const registries = useGamesRegistry();
  const roster = useHouseholdRoster();
  const [starting, setStarting] = useState(false);
  const [openPref, setOpen] = useState(() => readOpen(GAMES_OPEN_STORAGE_KEY, defaultOpen));
  const open = standalone || openPref;
  // Closed by default: a quiet game is out of the way, not gone (see `listOrder.ts`).
  const [quietOpen, setQuietOpen] = useState(() => readOpen(QUIET_OPEN_STORAGE_KEY, false));
  const [facts, setFacts] = useState<Record<string, GameListFacts>>({});
  const [unreadChats, setUnreadChats] = useState<Record<string, boolean>>({});
  const reportUnreadChat = useCallback((gameId: string, unread: boolean) => {
    setUnreadChats((prev) => (prev[gameId] === unread ? prev : { ...prev, [gameId]: unread }));
  }, []);
  const reportFacts = useCallback((gameId: string, next: GameListFacts) => {
    setFacts((prev) => {
      const cur = prev[gameId];
      if (cur && cur.isMyTurn === next.isMyTurn && cur.lastMoveAt === next.lastMoveAt) return prev;
      return { ...prev, [gameId]: next };
    });
  }, []);

  if (!user) return null;

  const myGames = registries.games.filter((g) => g.playerIds.includes(user.id));
  const active = myGames.filter((g) => g.status !== 'done');
  const finished = myGames.filter((g) => g.status === 'done');
  const { live, quiet } = arrangeActiveGames(active, facts, Date.now());
  const me = roster.getMember(user.id);

  const renderRow = (game: GameMeta): JSX.Element => {
    const opponentId = game.playerIds.find((id) => id !== user.id) ?? game.playerIds[0];
    return (
      <GameRow
        key={game.id}
        game={game}
        opponent={roster.getMember(opponentId, game.guestNames)}
        me={me}
        onFacts={reportFacts}
        onUnreadChat={reportUnreadChat}
      />
    );
  };

  const otherMembers = [...roster.members.values()].filter((m) => m.id !== user.id);

  return (
    <details
      className="flex flex-col gap-3"
      data-testid="games-section"
      open={open}
      onToggle={(e) => {
        const next = (e.currentTarget as HTMLDetailsElement).open;
        setOpen(next);
        writeOpen(GAMES_OPEN_STORAGE_KEY, next);
      }}
    >
      <summary
        className={`flex select-none list-none items-center justify-between [&::-webkit-details-marker]:hidden ${standalone ? 'pointer-events-none' : 'cursor-pointer'}`}
      >
        <h3 className="flex items-center gap-2 text-lg font-bold text-fg">
          <Gamepad2 size={18} aria-hidden="true" /> Games
          {!open && live.length > 0 && (
            <span className="text-sm font-medium text-fg-3">({live.length} going)</span>
          )}
          {!open && active.some((g) => unreadChats[g.id]) && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-inverse px-2 py-0.5 text-xs font-semibold text-inverse-fg"
              data-testid="games-new-chat"
            >
              <MessageCircle size={12} aria-hidden="true" /> New message
            </span>
          )}
        </h3>
        {!standalone && (
          <ChevronDown
            size={18}
            aria-hidden="true"
            className={`text-fg-3 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        )}
      </summary>
      <div className="mt-3 flex flex-col gap-3">
        {active.length === 0 && finished.length === 0 ? (
          <p className="text-sm text-fg-3">No games yet — start one below.</p>
        ) : (
          <div className="flex flex-col gap-2" data-testid="active-games-list">
            {live.map(renderRow)}
          </div>
        )}

        {/* Games nobody has moved in a week. A plain count, no "N days" — and any move
            lifts a game straight back up, because quiet is derived, never stored. */}
        {quiet.length > 0 && (
          <details
            className="text-sm text-fg-3"
            data-testid="quiet-games"
            open={quietOpen}
            onToggle={(e) => {
              const next = (e.currentTarget as HTMLDetailsElement).open;
              setQuietOpen(next);
              writeOpen(QUIET_OPEN_STORAGE_KEY, next);
            }}
          >
            <summary className="cursor-pointer select-none py-1 font-semibold text-fg-2">
              Quiet games · {quiet.length}
            </summary>
            <div className="mt-2 flex flex-col gap-2" data-testid="quiet-games-list">
              {quiet.map(renderRow)}
            </div>
          </details>
        )}

        {finished.length > 0 && (
          <details className="text-sm text-fg-3" data-testid="finished-games">
            <summary className="cursor-pointer select-none py-1 font-semibold text-fg-2">
              Finished ({finished.length})
            </summary>
            <div className="mt-2 flex flex-col gap-4" data-testid="finished-games-archive">
              {finished.length > ARCHIVE_GROUP_THRESHOLD
                ? groupGamesByMonth(finished).map((month) => (
                    <div key={month.key} className="flex flex-col gap-2">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-3">
                        {month.label}
                      </span>
                      <div className="flex flex-col gap-2">
                        {month.games.map((game) => (
                          <FinishedGameCard key={game.id} game={game} roster={roster} />
                        ))}
                      </div>
                    </div>
                  ))
                : sortFinishedNewestFirst(finished).map((game) => (
                    <FinishedGameCard key={game.id} game={game} roster={roster} />
                  ))}
            </div>
          </details>
        )}

        <button
          type="button"
          data-testid="new-game-button"
          className="flex h-12 items-center justify-center gap-2 rounded-md border-2 border-line-strong text-sm font-semibold text-fg"
          onClick={() => setStarting(true)}
        >
          <Plus size={16} /> Start a game
        </button>

        <StartGameSheet
          open={starting}
          onClose={() => setStarting(false)}
          userId={user.id}
          userName={user.name}
          members={otherMembers}
          onCreated={() => undefined}
        />
      </div>
    </details>
  );
}
